import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../contexts/AuthContext';
import AuthService from '../services/AuthService';

/**
 * Login page component
 * Handles user authentication with email and password
 */
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract query parameters
  const params = new URLSearchParams(location.search);
  const verifiedParam = params.get('verified');
  const resetParam = params.get('reset');
  
  // Redirect to home if already logged in
  useEffect(() => {
    if (user) {
      navigate('/school');
    }
    
    // Show toast message if coming from email verification
    if (verifiedParam === 'true') {
      toast.success('Email verified successfully! You can now log in.');
    }
    
    // Show toast message if coming from password reset
    if (resetParam === 'true') {
      toast.success('Password reset successfully! You can now log in with your new password.');
    }
  }, [user, navigate, verifiedParam, resetParam]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }
    
    try {
      setLoading(true);
      
      // Make the API call directly to handle the status response
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      // Check for alumni verification requirement
      if (response.status === 403 && data.requiresAlumniVerification) {
        toast.error('Alumni verification required. Check your email for a verification code.');
        navigate('/verify-alumni', { replace: true });
        return;
      }

      // Check for suspended or banned account
      if (response.status === 403 && (data.status === 'suspended' || data.status === 'banned')) {
        // Store status details for the status page
        localStorage.setItem('statusDetails', JSON.stringify({
          status: data.status,
          until: data.until,
          reason: data.reason,
          changedAt: data.changedAt
        }));
        
        toast.error(`Your account has been ${data.status}`);
        
        // Redirect to status page
        navigate('/account-status', { replace: true });
        return;
      }
      
      // For other errors, throw with the error message
      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }
      
      // Handle successful login - save the token
      localStorage.setItem('token', data.token);
      
      // Save remembered email if checked
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      
      // Call the login function from AuthContext with user data and token
      login(data.user, data.token);
      
      toast.success('Login successful!');
      
      // Navigate to school page
      setTimeout(() => {
        navigate('/school');
      }, 1500);
    } catch (error) {
      console.error('Login error:', error);
      
      // Error message handling
      let errorMessage = 'Failed to login. Please check your credentials.';
      
      if (error.message) {
        if (error.message.includes('session has expired')) {
          errorMessage = 'User account not found. Please check your email or register a new account.';
        } else if (error.message.includes('password') || error.message.toLowerCase().includes('invalid')) {
          errorMessage = 'Invalid email or password. Please try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };
  
  // Load remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <div className="text-4xl font-bold text-red-500">EDX</div>
            <div className="text-4xl font-bold text-black dark:text-white">Red</div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Or{' '}
            <Link to="/register" className="font-medium text-red-600 hover:text-red-500">
              create a new account
            </Link>
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white dark:bg-gray-800 rounded-t-md focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white dark:bg-gray-800 rounded-b-md focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                placeholder="Password"
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-700 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                Remember me
              </label>
            </div>
            
            <div className="text-sm">
              <Link to="/reset-password" className="font-medium text-red-600 hover:text-red-500">
                Forgot your password?
              </Link>
            </div>
          </div>
          
          <div>
            <button
              type="submit"
              disabled={loading}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                loading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                <LogIn className="h-5 w-5 text-red-500 group-hover:text-red-400" />
              </span>
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
        
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            By signing in, you agree to the{' '}
            <Link to="/terms" className="font-medium text-red-600 hover:text-red-500">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="font-medium text-red-600 hover:text-red-500">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;